import {t, Trans} from '@lingui/macro'
import {AnimatePresence, motion} from 'framer-motion'

import {ModalContent, Stack, Text, Button, Box} from '@pleo-io/telescope'

import type {PricingPlan} from '~/entitlement/use-pricing-plan'
import {variants, useGuide} from '~/feature-ui/guides/guide'
import {
    Modal,
    ScreenWrapper,
    ScreenContentWrapper,
    IllustrationWrapper,
    IconLeft,
} from '~/feature-ui/guides/guide.styles'
import useConditionedPlans from '~/feature-ui/plan-pill/use-conditioned-plans'
import {Steps} from '~/generic-ui/steps'
import type Country from '~/locale/country'
import tracking from '~/tracking'

import {getSteps} from './how-trial-works.helpers'
import {ActionWrapper, ContentWrapper} from './how-trial-works.styles'

import type {TrialActivationBannerActionedExtended} from '../../trial/types'

interface HowTrialWorksGuideProps {
    onDone: () => void
    onDismiss: () => void
    pickedPlan: PricingPlan
    trialLength: number
    country: Country
    isFdd: boolean
}
export const HowTrialWorksGuide = ({
    onDone,
    onDismiss,
    pickedPlan,
    trialLength,
    country,
    isFdd,
}: HowTrialWorksGuideProps) => {
    const conditionedPlans = useConditionedPlans()
    const steps = getSteps({pickedPlan, trialLength, country, isFdd, conditionedPlans})

    const {active, onSetActiveStep, onNextStep, onPreviousStep} = useGuide({
        steps,
        onClose: onDismiss,
    })

    const activeStep = steps[active]
    const isLastStep = active === steps.length - 1
    const showBackButton = active > 0

    const handleCrumbSelect = (index: number) => {
        onSetActiveStep(index)
    }

    const renderNextButton = () => {
        if (isLastStep) {
            if (!isFdd) {
                return (
                    <Button
                        variant="primary"
                        onClick={() => {
                            tracking.trialActivationBannerActioned({
                                action: 'activate_account',
                            } as TrialActivationBannerActionedExtended)
                            onDone()
                        }}
                    >
                        <Trans>Activate account</Trans>
                    </Button>
                )
            }

            return (
                <Button
                    variant="primary"
                    onClick={() => {
                        tracking.trialActivationBannerActioned({
                            action: 'wallet',
                        } as TrialActivationBannerActionedExtended)
                        onDone()
                    }}
                >
                    <Trans>Go to Pleo Wallet</Trans>
                </Button>
            )
        }

        return (
            <Button
                variant="primary"
                onClick={() => {
                    tracking.trialActivationBannerActioned({
                        action: 'next_clicked',
                    } as TrialActivationBannerActionedExtended)
                    onNextStep()
                }}
            >
                <Trans>Next</Trans>
            </Button>
        )
    }

    return (
        <Modal
            isOpen={true}
            onDismiss={() => {
                tracking.trialActivationBannerActioned({
                    action: 'closed',
                } as TrialActivationBannerActionedExtended)
                onDismiss()
            }}
            aria-label={t`Modal explaining how the trial works`}
        >
            <ModalContent align="left" as="section">
                <AnimatePresence initial={false} exitBeforeEnter>
                    <motion.div
                        key={activeStep.key}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                    >
                        <ScreenWrapper>
                            <ScreenContentWrapper>
                                <Box p={40}>
                                    <Stack space={40}>
                                        <Steps>
                                            {steps.map((step, index) => (
                                                <Steps.Item
                                                    key={step.name}
                                                    onClick={() => handleCrumbSelect(index)}
                                                    isSelected={active === index}
                                                >
                                                    {step.name}
                                                </Steps.Item>
                                            ))}
                                        </Steps>
                                        <ContentWrapper>
                                            <Text variant="4xlarge-accent" as="h2">
                                                {activeStep.title}
                                            </Text>
                                            {activeStep.sections?.map(({key, content}) => (
                                                <section key={key}>{content}</section>
                                            ))}
                                        </ContentWrapper>
                                    </Stack>
                                </Box>
                                <IllustrationWrapper $color={activeStep.illustrationBgColor}>
                                    {activeStep.illustration}
                                </IllustrationWrapper>
                            </ScreenContentWrapper>

                            <ActionWrapper
                                $justifyContent={showBackButton ? 'space-between' : 'end'}
                            >
                                {showBackButton && (
                                    <Button
                                        variant="link"
                                        onClick={() => {
                                            tracking.trialActivationBannerActioned({
                                                action: 'back_clicked',
                                            } as TrialActivationBannerActionedExtended)
                                            onPreviousStep()
                                        }}
                                    >
                                        <IconLeft />
                                        <Trans>Back</Trans>
                                    </Button>
                                )}
                                {renderNextButton()}
                            </ActionWrapper>
                        </ScreenWrapper>
                    </motion.div>
                </AnimatePresence>
            </ModalContent>
        </Modal>
    )
}
